import React from "react";
import FormattedMessage from "components/FormattedMessage";
import Hide from "hidden-styled";
import CirclesLifeLogo from "assets/images/circles-life-logo.png";
import LiveOnLogo from "assets/images/live-on-logo.png";
import PovoLogo from "assets/images/povo-logo.png";
import onicLogo from "assets/images/onic.png";
import CountryXLogo from "assets/images/countryx-logo.png";
import { ReactComponent as IconHelp } from "assets/icons/icon-help-white-filled.svg";
import { ReactComponent as IconChat } from "assets/icons/icon-single-line-chat-white-filled.svg";
import { Menu } from "components/Icons";
import { Button, Box, Flex, Text } from "ui-components";
import { activateZendeskWidget, gotoUrl, formatPathUrl } from "utils/helpers";
import { BRAND_NAME } from "utils/constants";
import get from "lodash/get";
import map from "lodash/map";
import isEmpty from "lodash/isEmpty";
import { buttonThemes, getButtonColorVariant } from "ui-components/theme";
import ProfileIcon from "components/ProfileIcon";
import { useSelector } from "react-redux";
import {
  selectzEIdentifyUserData,
  makeSelectPageData,
  makeSelectUserOrders,
  makeSelectIsUserLoggedIn,
  makeSelectLoggedInUserDetails,
  makeSelectOrderRef
} from "ducks/selectors";
import { getGTMtagdata } from "analytics/utils/helpers";
import { isMiniApp, isNative } from "utils/localStorage";
import { makeSelectExternalPendingOrders } from "ducks/selectors/order";

const Logos = {
  "circles.life": CirclesLifeLogo,
  "live.on": LiveOnLogo,
  povo: PovoLogo,
  countryx: CountryXLogo,
  onic: onicLogo
};
const HelpIcons = {
  help: IconHelp,
  chat: IconChat
};
const brandLogo = BRAND_NAME && Logos[BRAND_NAME.toLowerCase()];

const Header = ({
  setOpen,
  buttons,
  appSettings,
  themeColors,
  userDetails,
  ...props
}) => {
  const miniApp = isMiniApp();
  const buttonTheme = get(themeColors, "button") || buttonThemes.blue;
  const helpTheme = get(appSettings, "layout.header.help.theme") || buttonTheme;
  const helpText = get(appSettings, "layout.header.help.text", "help");
  const helpIcon = get(appSettings, "layout.header.help.icon", "help");
  const HelpIcon = get(HelpIcons, helpIcon, IconHelp);
  const helpHidden =
    miniApp || get(appSettings, "layout.header.help.hidden", false);
  const pageLogoUrl = get(useSelector(makeSelectPageData()), "pageLogoUrl");
  const externalPendingOrders = useSelector(makeSelectExternalPendingOrders());
  const logoImageUrl = get(appSettings, "layout.header.logo.url");
  const marketingPageUrl = get(appSettings, "layout.header.marketingPageUrl");
  const profileImageUrl = get(userDetails, "profile_image");
  const zEIdentify = useSelector(selectzEIdentifyUserData);
  const prefilzEForm = get(
    appSettings,
    "layout.header.help.prefilZendeskForm",
    true
  );
  const Logo = logoImageUrl || brandLogo || CirclesLifeLogo;
  const userOrders = useSelector(makeSelectUserOrders());
  const isLoggedIn = useSelector(makeSelectIsUserLoggedIn());
  const loggedInUserDetails = useSelector(makeSelectLoggedInUserDetails());
  const orderRef = useSelector(makeSelectOrderRef());
  const containerName = formatPathUrl(window.location.pathname);

  const setupAndActivateZendeskWidget = () => {
    if (prefilzEForm) {
      activateZendeskWidget(zEIdentify);
    } else {
      activateZendeskWidget();
    }
  };

  return (
    <>
      <Flex
        as="nav"
        className="app-page-header"
        sx={{
          zIndex: 1000,
          position: "fixed",
          width: "100%",
          backgroundColor: "white",
          boxShadow: "0 4px 5px -3px rgba(0, 0, 0, 0.2)"
        }}
        height={77}
        justifyContent="space-between"
        alignItems="center"
      >
        <Flex alignItems="center">
          <Box mx={4} sx={{ cursor: "pointer" }}>
            {!isEmpty(buttons) && (
              <Hide
                md
                lg
                data-testid={"hide-header"}
                onClick={() => setOpen(true)}
              >
                <Menu />
              </Hide>
            )}
          </Box>

          {externalPendingOrders && externalPendingOrders.length > 0 ? (
            <Box
              as="span"
              data-gtmdata={getGTMtagdata(
                containerName,
                "onic_icon",
                loggedInUserDetails,
                null,
                orderRef,
                null
              )}
            >
              <img
                style={{ height: "2rem" }}
                alt={BRAND_NAME}
                src={Logo}
                data-gtmdata={getGTMtagdata(
                  "purchase_flow_started",
                  "onic_icon",
                  loggedInUserDetails,
                  null,
                  orderRef,
                  null
                )}
              />
            </Box>
          ) : (
            <Box
              as="a"
              href={pageLogoUrl || marketingPageUrl}
              data-gtmdata={getGTMtagdata(
                "purchase_flow_started",
                "onic_icon",
                loggedInUserDetails,
                null,
                orderRef,
                null
              )}
            >
              <img
                style={{ height: "2rem", cursor: "pointer" }}
                alt={BRAND_NAME}
                src={Logo}
                data-gtmdata={getGTMtagdata(
                  "purchase_flow_started",
                  "onic_icon",
                  loggedInUserDetails,
                  null,
                  orderRef,
                  null
                )}
              />
            </Box>
          )}
        </Flex>

        <Flex mx={2} justifyContent="space-around">
          <Hide sm xs>
            <Box mx={4}>
              {map(buttons, ({ label, url, variant }, index) => (
                <Button
                  key={index}
                  mx={1}
                  variant={variant}
                  sx={getButtonColorVariant(buttonTheme, variant)}
                  onClick={() => gotoUrl(url, false, {}, true)}
                  data-testid={"header-button-" + index}
                >
                  <FormattedMessage id={label} />
                </Button>
              ))}
            </Box>
          </Hide>
        </Flex>

        <Flex alignItems="center">
          {!isNative() && !helpHidden && !isEmpty(userOrders) && isLoggedIn && (
            <Flex
              width={[145, 175]}
              mx={4}
              alignItems="center"
              justifyContent="flex-end"
              marginRight="10px"
            >
              <Flex
                mx={2}
                alignItems="center"
                justifyContent="space-between"
                sx={getButtonColorVariant(helpTheme, "primary")}
                style={{
                  cursor: "pointer",
                  borderRadius: "25px",
                  padding: 4,
                  height: "35px"
                }}
                data-testid={"zendesk-button"}
                onClick={setupAndActivateZendeskWidget}
              >
                <Box>
                  <HelpIcon />
                </Box>
                {helpText && (
                  <Box
                    sx={{
                      pl: 1,
                      pr: 3,
                      fontSize: ["0.8rem", "0.9rem", "1rem"]
                    }}
                  >
                    <Text style={{ color: "#FFFFFF" }}>
                      <FormattedMessage id={helpText} />
                    </Text>
                  </Box>
                )}
              </Flex>
            </Flex>
          )}
          {!isNative() && (
            <ProfileIcon
              {...props}
              visibility={props.isLoggedIn}
              data={props.data}
              profileImageUrl={profileImageUrl}
              themeColors={themeColors}
              gtmdata={{
                containerName: "profile-image",
                altContainerName: null
              }}
              isLoggedIn={isLoggedIn}
            />
          )}
        </Flex>
      </Flex>
    </>
  );
};

Header.defaultProps = {};

export default Header;
